import { useState, useEffect, useContext } from 'react';
import { Table, Header, HeaderRow, HeaderCell, Body, Row, Cell } from '@table-library/react-table-library/table';
import { usePagination } from '@table-library/react-table-library/pagination';
import { useSort, SortToggleType } from '@table-library/react-table-library/sort';
import { useTheme } from '@table-library/react-table-library/theme';
import { AppContext } from '../util/AppContext';
import { getConfigValue } from '../util/getConfigValue';
import PropType from 'prop-types';
import { Pagination } from '../components/Pagination';
import { getSanitizedHtml } from "../util/getSanitizedHtml";
import { getBorderColorClass } from '../util/getBorderColor'
import BatchPrintRowOptionsMenu from './BatchPrintRowOptionsMenu';
import { compareForDynamicTable } from '../util/dynamicTableSort.js';
import { FavoriteDesignation } from './FavoriteDesignation.js';
import { getTableColumnsSpec } from '../util/getTableColumnsSpec.js';

BatchPrintList.propTypes = {
    headers: PropType.array,
    batchRows: PropType.array,
    selectedBatch: PropType.array,
    handleAddClick: PropType.func,
    handleRemoveClick: PropType.func,
    onOptionsClick: PropType.func,
    updateTargetRecords: PropType.func,
    displayFavoriteDesignation: PropType.bool,
    pageResetTrigger: PropType.number
}

function BatchPrintList({ headers, batchRows, handleAddClick, handleRemoveClick, selectedBatch, onOptionsClick, updateTargetRecords, displayFavoriteDesignation, pageResetTrigger }) {
    const [appState] = useContext(AppContext);
    const [data, setData] = useState({ nodes: [] });
    const [numberOfPages, setNumberOfPages] = useState(0);

    // Pagination
    const pagination = usePagination(
        data,
        {
            state: {
                page: 0,
                size: appState.config?.BatchPrint?.Page?.MaxNumber?.Display?.Value
            },
        },
    );

    /* THEME */
    const THEME = {
        Row: `
        &.target-table-indicator-success .td:first-of-type{
            border-left: 2px solid green;
        }
      `,
        BaseCell: `
      & > div {
        overflow: visible;
      }
    `
    };
    const columnsSpec = getTableColumnsSpec(headers);
    const themeObject = useTheme({...THEME, Table: `
        --data-table-library_grid-template-columns: 4% ${columnsSpec} 10%;
    `});

    const themeColumns = useTheme(themeObject);
    

    //build sort functions object
    const sortFunctions = {};
    for (const header of headers) {
        sortFunctions[header.key] = (array) => array.sort((a, b) => compareForDynamicTable(header.key, header.sortField, false, a, b));
    }

    let defaultSortKey = "displayName";
    if (headers && headers.length > 0) {
        defaultSortKey = headers[0].key;
    }

    /* SORT */
    const sort = useSort(
        data,
        {
            state:
            {
                sortKey: defaultSortKey,
                reverse: false,
            }
        },
        {
            sortFns: sortFunctions,
            sortToggleType: SortToggleType.AlternateWithReset
        }
    );

    //TODO move out inline style into its own class
    const setActiveIconColor = {
        color: "white"
    }
    const getIcon = (sortKey) => {
        if (sort.state.sortKey === sortKey && sort.state.reverse) {
            return <span className='fa-solid fa-arrow-up-short-wide' style={setActiveIconColor}></span>;
        }

        if (sort.state.sortKey === sortKey && !sort.state.reverse) {
            return <span className="fa-solid fa-arrow-down-short-wide" style={setActiveIconColor}></span>;
        }

        return <span className='fa-solid fa-sort' />;
    };

    useEffect(() => {
        if (batchRows) {
            setData({ nodes: batchRows })
            const pageSize = parseInt(getConfigValue(appState.config?.BatchPrint?.Page?.MaxNumber?.Display, appState)?.Value, 10);
            let pages = Math.floor(batchRows.length / pageSize);
            pages = batchRows.length % pageSize > 0 ? pages + 1 : pages;
            setNumberOfPages(pages);
            if (pagination.state.page >= pages) {
                pagination.fns.onSetPage(Math.max(0, pages - 1));
            }
        }
    }, [batchRows])

    useEffect(() => {
        pagination.fns.onSetPage(0);
    }, [pageResetTrigger])

    function handleIncludeTargetNameClick(item, event) {
        const newItem = { ...item, includeTargetName: event.target.checked }

        if (!onOptionsClick(newItem)) {
            event.preventDefault();
            return false;
        }

        return true;
    }


    function handleFavoriteClick(hcp) {
        if (!updateTargetRecords) {
            return;
        }		
        updateTargetRecords(hcp);  
	}

    return (
        <div className="container-xl mt-5 mb-5 mb-lg-7">
            <div className="row justify-content-center">
                <div className="col-12">
                    {headers &&
                        <Table className='table pd-table pod-table-hcp pod-table-batchprint' data={data} sort={sort} theme={themeColumns} layout={{ custom: true, isDiv: true }} pagination={pagination}>
                            {(tableList) => (
                                <>
                                    <Header>
                                        <HeaderRow className='table-dark d-none d-md-contents'>
                                            <HeaderCell className='px-2 py-2' scope='col'></HeaderCell>
                                            {headers.map((header) =>
                                                <HeaderCell className='px-2 py-2' key={header.key} scope='col'>
                                                    {header.title}<button
                                                        className='btn-ghost btn-table'
                                                        children={getIcon(header.key)}
                                                        onClick={() =>
                                                            sort.fns.onToggleSort({
                                                                sortKey: header.key,
                                                            })
                                                        }
                                                    ></button>
                                                </HeaderCell>)}
                                            <HeaderCell scope='col'></HeaderCell>
                                        </HeaderRow>
                                    </Header>

                                    <Body>
                                        {tableList.map((item) => (
                                            <Row key={item.data.id}
                                                item={item}
                                                className={`row pd-plan-table-row ${getBorderColorClass(item.data.hasWinPlans)}`}>
                                                <Cell>
                                                    <div className='input-container'>
                                                        {displayFavoriteDesignation && <FavoriteDesignation
                                                            hcp={item.data}
                                                            updateCallback={() => handleFavoriteClick(item.data)}
                                                        />}
                                                    </div>
                                                </Cell>
                                                {Object.keys(item.columns).map((key) => <Cell className='px-2 py-2' key={key}>
                                                    {<span className="d-block d-md-none color-grey-d1">{item.columns[key] ? headers.find(h => h.key === key)?.title : ""}</span>}
                                                    <span className="form-check-label" dangerouslySetInnerHTML={getSanitizedHtml(item.columns[key])}></span></Cell>)}
                                                <Cell>
                                                    <div className="pd-plan-table-card justify-content-end" style={{ boxShadow: 'none' }}>
                                                        <BatchPrintRowOptionsMenu
                                                            item={{...item, includeTargetName: selectedBatch.find((d) => d.data.id === item.data.id)?.includeTargetName ?? false}}
                                                            onOptionsClick={handleIncludeTargetNameClick}
                                                        />

                                                        {/* Add/Remove HCP button */}
                                                        {!selectedBatch.find((d) => d.data.id === item.data.id) ?
                                                            <button type="button" className="btn btn-outline-primary btn-icon-only ms-3"
                                                                onClick={(event) => handleAddClick(item, event)}>
                                                                <span className="fa-regular fa-plus btn-icon"></span>
                                                            </button>
                                                            :
                                                            <button type="button" className="btn btn-outline-danger btn-icon-only ms-3"
                                                                onClick={(event) => handleRemoveClick(item, event)}>
                                                                <span className="fa-regular fa-minus btn-icon"></span>
                                                            </button>
                                                        }
                                                    </div>
                                                </Cell>
                                            </Row>
                                        ))}
                                    </Body>
                                </>
                            )}
                        </Table>
                    }
                    {numberOfPages > 1 && pagination && <Pagination numberOfPages={pagination.state.getTotalPages(data.nodes)} currentPage={pagination.state.page} setCurrentPage={(index, e) => {
                        pagination.fns.onSetPage(index);
                    }} />}
                </div>
            </div>
        </div>
    )
}

export default BatchPrintList;
